<template>
  <section test-id="main">
    <div class="flex flex-col items-center py-2 justify-center gap-y-2 mt-8">
      <h3 class="text-2xl font-semibold mt-1">
        {{
          parseMoneyAll(
            transactionInfo.transferAmount.amount,
            transactionInfo.transferAmount.currency
          )
        }}
      </h3>
      <p class="text-sm font-normal">
        {{ transactionInfo.transferDeposit.currency }}
        {{ addComma(transactionInfo.transferDeposit.amount.toFixed(2)) }}
      </p>
      <p
        class="text-xs text-center font-normal rounded-md px-1 py-2 capitalize w-24 mt-3 mb-2"
        :class="statusClass(transactionInfo.transferStatus)"
      >
        {{ transactionInfo.transferStatus }}
      </p>
    </div>
    <div class="text-fg-muted">
      <ul>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 lg:py-6"
        >
          <span>Time & Date</span>
          <span class="">{{ displayTimeDate }}</span>
        </li>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 lg:py-6"
        >
          <span>Transaction ID</span>
          <span
            >{{ maskID(transactionInfo.transferID) }}
            <span
              class="cursor-pointer mb-1 ml-2"
              v-clipboard:copy="transactionInfo.transferID"
              v-clipboard:success="onCopy"
              v-clipboard:error="onError"
            >
              <copy-icon />
            </span>
          </span>
        </li>
        <li
          class="flex justify-between font-normal text-sm items-center border-b py-3.5 lg:py-5"
        >
          <span>Reason For Transfer</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showReasonForTransfer')"
            >{{ showReasonForTransfer ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showReasonForTransfer">
            <li
              class="items-center text-sm border rounded-lg font-normal border-b py-3.5 lg:py-4 my-3 bg-neutral px-3"
            >
              <span style="word-break: normal">{{
                transactionInfo.transferReason
              }}</span>
            </li>
          </div>
        </transition>
        <li
          class="grid grid-cols-2 items-center font-normal text-sm border-b py-3.5 lg:py-6"
        >
          <span>From</span>
          <span class="flex items-center gap-x-2">
            <currency-icons
              :currency="
                getCurrencyCountry(transactionInfo.transferDeposit.currency)
              "
            />
            <span class="capitalize">{{
              transactionInfo.transferDeposit.currency
            }}</span>
          </span>
        </li>
        <li
          class="grid grid-cols-2 items-center border-b font-normal text-sm py-3.5 lg:py-6"
        >
          <span>To</span>
          <span class="flex items-center gap-x-2">
            <currency-icons
              :currency="
                getCurrencyCountry(transactionInfo.transferAmount.currency)
              "
            />
            <span class="capitalize">{{
              transactionInfo.transferAmount.currency
            }}</span>
          </span>
        </li>
        <li
          class="flex justify-between font-normal text-sm items-center rounded-md border-t py-3.5 lg:py-5"
        >
          <span>Exchange Details</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showExchangeDetails')"
            >{{ showExchangeDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showExchangeDetails">
            <div class="pt-3">
              <div
                class="flex flex-row rounded-md border py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Amount converted</p>
                <p class="text-sm font-semibold">
                  {{ transactionInfo.transferDeposit.currency }}
                  {{
                    addComma(transactionInfo.transferDeposit.amount.toFixed(2))
                  }}
                </p>
              </div>
              <div
                class="flex flex-row rounded-md border py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">Exchange rate</p>
                <p class="text-sm font-semibold">
                  {{ transactionInfo.transferExchangeRate }}
                </p>
              </div>
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin">
                  <span
                    v-if="transactionInfo.transferDestination.personalDetail"
                  >
                    {{ transactionInfo.transferDestination.personalDetail }}
                  </span>
                  <span v-else>Recipient</span>
                  gets
                </p>
                <p class="text-sm font-bold">
                  {{
                    parseMoneyAll(
                      transactionInfo.transferAmount.amount,
                      transactionInfo.transferAmount.currency
                    )
                  }}
                </p>
              </div>
            </div>
          </div>
        </transition>
        <li
          class="flex justify-between font-normal text-sm items-center rounded-md border-t py-3.5 lg:py-5"
        >
          <span>Recipient Details</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showRecipientDetails')"
            >{{ showRecipientDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showRecipientDetails">
            <!-- bank details -->
            <div
              v-if="transactionInfo.transferDestination.bankDetails"
              class="pt-3"
            >
              <div v-for="field in bankDetails" :key="field.key">
                <div
                  v-if="valueExists(field.key)"
                  class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
                >
                  <p class="text-sm font-thin">{{ field.label }}</p>
                  <p class="text-sm font-semibold">
                    {{ field.key || '--' }}
                    <span
                      v-if="field.copy"
                      class="cursor-pointer mb-1 ml-2"
                      v-clipboard:copy="field.key"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      <copy-icon />
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <!-- wallet details -->
            <div
              v-if="transactionInfo.transferDestination.walletDetails"
              class="pt-3"
            >
              <div v-for="field in walletDetails" :key="field.key">
                <div
                  v-if="valueExists(field.key)"
                  class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
                >
                  <p class="text-sm font-thin">{{ field.label }}</p>
                  <p class="text-sm font-semibold">
                    {{ field.key || '--' }}
                    <span
                      v-if="field.copy"
                      class="cursor-pointer mb-1 ml-2"
                      v-clipboard:copy="field.key"
                      v-clipboard:success="onCopy"
                      v-clipboard:error="onError"
                    >
                      <copy-icon />
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </ul>
    </div>
    <div v-if="isCompletedTransaction">
      <button
        class="w-full flex justify-center gap-x-2 bg-green-6 text-green-1 border border-green-1 py-4 rounded-lg"
        @click="generateReceipt"
      >
        <download-icon />
        {{ isLoading ? 'Downloading...' : 'Download Receipt' }}
      </button>
    </div>
    <vuehtml2pdf
      :manual-pagination="true"
      :filename="fileName"
      :pdf-quality="2"
      pdf-format="a4"
      pdf-content-width="800px"
      :enable-download="true"
      ref="DownloadComp"
      :show-layout="false"
    >
      <section slot="pdf-content">
        <generate-receipt-details
          :receiptDetails="this.receiptDetails"
          :transferType="'Transfer'"
        />
      </section>
    </vuehtml2pdf>
  </section>
</template>
<script>
import {
  AddComma,
  ParseLocalTime,
  ParseMoneyAll,
  GetCurrencyCountry,
  MaskWalletAddressTwo,
  StatusClass,
  FormatServerName
} from '@/utils/helpers.js'
import { ADMIN_BUSINESS_NAME } from '@/utils/constants'
import getReceiptDetails from '@/mixins/global.js'
import userKinds from '@/mixins/userKinds.js'
import app from '@/mixins/app.js'
export default {
  mixins: [getReceiptDetails, userKinds, app],
  data () {
    return {
      showReasonForTransfer: false,
      showExchangeDetails: false,
      showRecipientDetails: false,
      payload: {
        id: ''
      },
      receiptDetails: {},
      isLoading: false
    }
  },
  methods: {
    valueExists (val) {
      return val && val.length > 0
    },
    formatServerName (name) {
      return FormatServerName(name)
    },
    statusClass (status) {
      return StatusClass(status)
    },
    toggleShow (arg) {
      this[arg] = !this[arg]
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    addComma (val) {
      return AddComma(val.toString())
    },
    ParseLocalTime (time) {
      return ParseLocalTime(time)
    },
    maskID (val) {
      return MaskWalletAddressTwo(val)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    generateReceipt () {
      this.payload.id = this.transactionInfo.transferID
      this.getReceiptDetails(this.payload)
    }
  },
  components: {
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    Transition: () =>
      import('@/components/v2/animation/transition/Transition.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    DownloadIcon: () => import('@/assets/v2/icons/CloudDownloadIcon.vue'),
    GenerateReceiptDetails: () =>
      import('@/components/common/GenerateReceiptDetails.vue')
  },
  computed: {
    walletDetails () {
      let data = []
      const fieldName = this.transactionInfo?.transferDestination?.walletDetails
      data = [
        {
          key: fieldName.assetType,
          label: 'Asset Type',
          copy: true
        },
        {
          key: fieldName.network,
          label: 'Network',
          copy: true
        },
        {
          key: fieldName.walletAddress,
          label: 'Wallet Address',
          copy: true
        }
      ]
      return data
    },
    bankDetails () {
      let data = []
      const fieldName = this.transactionInfo?.transferDestination?.bankDetails

      data = [
        { key: fieldName?.bankName, label: 'Bank Name', copy: true },
        { key: fieldName?.accountNumber, label: 'Account Number', copy: true },
        { key: fieldName?.accountName, label: 'Account Name', copy: true },
        {
          key: fieldName?.bankPhone,
          label: 'Bank Phone',
          copy: true
        },
        { key: fieldName?.bankAddress, label: 'Bank Address', copy: true },
        { key: fieldName?.routingNumber, label: 'Routing Number', copy: true }
      ]
      return data
    },
    isHoldingsTransfer () {
      return this.transactionInfo.transferSource === 'treasury'
    },
    historyStatus () {
      return (
        this.transactionInfo.transferStatus === this.$STATUS.COMPLETED ||
        this.transactionInfo.transferStatus === this.$STATUS.FAILED ||
        this.transactionInfo.transferStatus === this.$STATUS.CANCELLED
      )
    },
    transactionInfo () {
      return this.$store.state.transactions.transactionDetails
    },
    displayTimeDate () {
      const timeToPick = this.historyStatus
        ? this.transactionInfo.transferCreatedAt
        : this.transactionInfo.transferCompletedAt

      return this.tooltipDateContent(timeToPick)
    },
    isCompletedTransaction () {
      return this.transactionInfo.transferStatus === this.$STATUS.COMPLETED
    },
    fileName () {
      return `${this.businessName || this.adminBusinessName} - (${
        this.transactionInfo.transferAmount.amount
      } ${this.transactionInfo.transferAmount.currency})`
    }
  }
}
</script>
